/**
 * Teamspace SCOPE chooser — the shared list body, plus the one visibility predicate every shell
 * consumes (spec `26-spec-topnav-0826.md` §3/§4, T981/T983).
 *
 * Two shells render this exact list, so the [ALL] entry, its `SHOW_ALL_VIEW` /
 * `selectIsAllViewAvailable` gating and the ROOT→"Main Team" relabelling exist HERE only:
 *  - `TeamSwitcher`     — the Radix sub-menu shell (inside the profile menu / EmergencyTopBar)
 *  - `TeamScopeButton`  — the standalone top-bar shell (its own trigger + DropdownMenu)
 *
 * Radix sub-menu parts (`DropdownMenuSub*`) only render inside a parent `DropdownMenu`, which is
 * why the top-bar control cannot reuse the sub-menu shell — hence one list, two shells rather than
 * two copies of the list.
 *
 * `DropdownMenuItem` is valid in both a `DropdownMenuSubContent` and a `DropdownMenuContent`, so
 * the rows themselves need no per-shell branching. Search state lives here and resets for free:
 * Radix unmounts either content container when the menu closes.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Search } from 'lucide-react';
import { DropdownMenuItem, DropdownMenuSeparator, Input, ScrollArea } from '@scio3/ui';
import { clearPersistedCache } from '@scio3/graphql-client';
import { useAuthStore } from '@/stores/auth-store';
import { selectIsAllTeamsScope, selectIsAllViewAvailable, useTeamStore } from '@/stores/team-store';
import { ALL_TEAMS_SCOPE } from '@/features/group-security/constants';
import { analyticsTeamSwitched } from '@/lib/analytics-service';
import { clearPersistedPaths } from '@/hooks/usePersistedPath';
import { useTeamNoun } from '@/hooks/useTeamNoun';

interface TeamScopeGuardInput {
  /** The user's teamspace reach (`me.teams`) — only the count matters. */
  teams: readonly unknown[];
  /** `company.feature.allowTeams` — the plan check. */
  allowTeams?: boolean;
}

/**
 * Is a teamspace scope chooser worth showing at all?
 *
 * Pure `teams.length <= 1` (spec §4): the old guard also required the single team to be ROOT
 * (`teams[0]._id === '1'`), so a user whose ONLY teamspace was a non-root team still saw a chooser
 * with nothing to choose. Plan check (`allowTeams`) kept.
 *
 * THE one predicate — every shell (and `EmergencyTopBar`'s divider) calls it rather than restating
 * the condition, so the guard can never disagree with itself again.
 */
export function canSwitchTeamScope({ teams, allowTeams }: TeamScopeGuardInput): boolean {
  return !!allowTeams && teams.length > 1;
}

/** Store-bound form of `canSwitchTeamScope` — what components should use. */
export function useCanSwitchTeamScope(): boolean {
  const allowTeams = useAuthStore((s) => s.company?.feature?.allowTeams);
  const teams = useTeamStore((s) => s.teams);
  return canSwitchTeamScope({ teams, allowTeams });
}

/**
 * The label for the CURRENT scope — "All Teamspaces" in the [ALL] scope, else the active teamspace
 * name (ROOT's sentinel `'Default'` rendered as the Main-Team label). Shared so both shells'
 * triggers say the same thing.
 */
export function useTeamScopeLabel(): string {
  const { t } = useTranslation();
  const { noun } = useTeamNoun();
  const teams = useTeamStore((s) => s.teams);
  const activeTeamId = useTeamStore((s) => s.activeTeamId);
  const isAllTeamsScope = useTeamStore(selectIsAllTeamsScope);

  // FIXED string, NOT `useTeamNoun`'s plural: "Teamspaces" is the product term the settings nav
  // settled on after the v4 rename, while the hook's Team/Workspace split is the older vocabulary
  // (it still governs every OTHER label here, so it is deliberately not used for this one).
  if (isAllTeamsScope) return t('All Teamspaces');

  const activeTeam = teams.find((team) => team._id === activeTeamId);
  // M10 — the root team's backend name is the SENTINEL 'Default' (see open-api `_getRootTeam`),
  // never a display string. Render the Main Team label for it; the "Default" BADGE on the
  // Teamspaces row is a different key (`t('Default')`) and deliberately still says Default.
  if (activeTeam?.name === 'Default') return t('Default {{noun}}', { noun });
  return activeTeam?.name?.trim() || t('Default {{noun}}', { noun });
}

export function TeamScopeList() {
  const { t } = useTranslation();
  const { noun, nounPluralLower } = useTeamNoun();
  const teams = useTeamStore((s) => s.teams);
  const activeTeamId = useTeamStore((s) => s.activeTeamId);
  const setActiveTeam = useTeamStore((s) => s.setActiveTeam);
  const setTeamFilter = useTeamStore((s) => s.setTeamFilter);
  // [ALL] view (Group Security §1 / T901) — shown to EVERYONE when the flag is on and the user
  // reaches more than one teamspace. No role gate, no plan gate; flip SHOW_ALL_VIEW to remove it.
  const isAllViewAvailable = useTeamStore(selectIsAllViewAvailable);
  const isAllTeamsScope = useTeamStore(selectIsAllTeamsScope);
  const [search, setSearch] = useState('');

  // Same fixed "All Teamspaces" string as the trigger (`useTeamScopeLabel`) — see the note there
  // for why this one label does NOT ride `useTeamNoun`.
  const allLabel = t('All Teamspaces');
  const searchTerm = search.trim().toLowerCase();
  const filteredTeams = teams.filter((team) => {
    if (!searchTerm) return true;
    const name = team.name === 'Default' ? t('Default {{noun}}', { noun }) : team.name;
    return name.toLowerCase().includes(searchTerm);
  });
  const showAllEntry =
    isAllViewAvailable && (!searchTerm || allLabel.toLowerCase().includes(searchTerm));

  /**
   * Enter the [ALL] scope. `activeTeamId` is deliberately left alone — it is the AUTH team (the
   * top-level `teamId` GraphQL arg), only the `query.teamId` FILTER is dropped. No analytics event:
   * `analyticsTeamSwitched` re-registers PostHog's `team_id` super property, and stamping 'all'
   * there would misattribute every later event.
   */
  function handleSwitchToAll() {
    setTeamFilter(ALL_TEAMS_SCOPE);
    resetTeamScopedState(ALL_TEAMS_SCOPE);
  }

  function handleSwitch(teamId: string) {
    analyticsTeamSwitched({ fromTeamId: activeTeamId ?? undefined, toTeamId: teamId });
    setActiveTeam(teamId);
    resetTeamScopedState(teamId);
  }

  function resetTeamScopedState(teamId: string) {
    window.dispatchEvent(new CustomEvent('team:switched', { detail: { teamId } }));

    // Clear team-scoped persisted data before navigation:
    // 1. Persisted folder paths (sessionStorage) — prevents restoring old team's folder
    clearPersistedPaths();
    // 2. Apollo cache (IndexedDB) — prevents stale data from previous team showing briefly
    clearPersistedCache();

    // Navigate to base path (strip query params like ?id=, ?path=, ?teamId=)
    // to prevent cross-team data leakage when switching teams
    const basePath = window.location.pathname;
    window.location.href = basePath;
  }

  return (
    <>
      <div className="flex items-center gap-2 border-b px-3 py-2">
        <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
        <Input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('Search {{noun}}...', { noun: nounPluralLower })}
          aria-label={t('Search {{noun}}...', { noun: nounPluralLower })}
          className="h-7 border-none bg-transparent shadow-none focus-visible:ring-0"
          onKeyDown={(e) => e.stopPropagation()}
          data-testid="team-switcher-search"
        />
      </div>
      <ScrollArea type="auto" viewportClassName="max-h-60" onWheel={(e) => e.stopPropagation()}>
        {/* [ALL] scope — FIRST entry, everyone, no role/plan gate (T901). */}
        {showAllEntry && (
          <>
            <DropdownMenuItem
              onClick={handleSwitchToAll}
              className="cursor-pointer"
              data-testid="team-switcher-all"
            >
              {isAllTeamsScope && <Check className="mr-2 h-4 w-4 shrink-0" />}
              <span className="truncate">{allLabel}</span>
            </DropdownMenuItem>
            {filteredTeams.length > 0 && <DropdownMenuSeparator />}
          </>
        )}
        {filteredTeams.length === 0 && !showAllEntry ? (
          <div className="px-3 py-4 text-center text-sm text-muted-foreground">
            {t('No {{noun}} found', { noun: nounPluralLower })}
          </div>
        ) : (
          filteredTeams.map((team) => (
            <DropdownMenuItem
              key={team._id}
              onClick={() => handleSwitch(team._id)}
              className="cursor-pointer"
              data-testid={`team-switcher-item-${team._id}`}
            >
              {!isAllTeamsScope && team._id === activeTeamId && (
                <Check className="mr-2 h-4 w-4 shrink-0" />
              )}
              <span className="truncate">
                {team.name === 'Default' ? t('Default {{noun}}', { noun }) : team.name}
              </span>
            </DropdownMenuItem>
          ))
        )}
      </ScrollArea>
    </>
  );
}
